import React, { useEffect, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import { format } from 'date-fns';
import { uniq } from 'lodash';
import { Icon, Spinner, IconButton } from 'components';
import { ActionMenu } from 'components/ActionMenu/ActionMenu.component';
import { TextArea } from 'components/form';
import { ActionOption } from 'components/ActionMenu';
import TouchpointMessage from 'components/TouchpointMessage';
import { useCandidateMessage } from 'lib/providers/CandidateMessageProvider';
import { RoomAttributes } from 'lib/models/candidate-messages';
import {
  MessageAttributes,
  TouchpointMessageResponse,
} from 'lib/models/message';
import { OpportunityResponse } from 'lib/models/opportunity';
import { parseResponse } from 'lib/utils/parser';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { URLREGEXPATTERN } from 'lib/constants';
import { MessageAvatar } from '../MessageAvatar/MessageAvatar.component';
import { getDisplayName, getProcessedDate } from '../messagesUtils';
import { ChatBubble } from '../ChatBubble/ChatBubble.component';
import { ReceiverInfoCard } from '../ReceiverInfoCard/ReceiverInfoCard.component';

import styles from './ChatWindow.module.scss';

interface ChatWindowProps {
  className?: string;
  activeRoom: RoomAttributes | null;
  openParticipantsModal: () => void;
}

export const ChatWindow: React.FC<ChatWindowProps> = (
  props: ChatWindowProps
) => {
  const { className, activeRoom: roomData, openParticipantsModal } = props;
  const endPageRef = useRef<HTMLDivElement | null>(null);

  const {
    onMuteConversation,
    onDeleteConversation,
    onBlockConversation,
    fetchRoomMessages,
    sendMessage,
    isLoading,
    rooms,
    isMessagesFetching,
    getTouchpointData,
    touchpointCachedData,
  } = useCandidateMessage();

  const [inputText, setInputText] = useState<string>('');
  const [activeRoom, setActiveRoom] = useState<RoomAttributes>();
  const [messagesData, setMessagesData] = useState<MessageAttributes[]>([]);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  useEffect(() => {
    if (roomData) {
      setActiveRoom(roomData);
      setInputText('');
      loadMessages(roomData.id);
    }
  }, [roomData]);

  useEffect(() => {
    if (!isLoading && activeRoom) {
      const data = rooms.find(
        (room) => activeRoom && room.id === activeRoom.id
      );
      setActiveRoom(data);
    }
  }, [isLoading, rooms]);

  const displayName = useMemo(() => {
    if (activeRoom) {
      return getDisplayName(activeRoom);
    }
  }, [activeRoom]);

  const loadMessages = async (roomId?: string) => {
    const data = await fetchRoomMessages(
      roomId || (activeRoom && activeRoom.id) || ''
    );
    setMessagesData(data || []);
    getSlugData(data || []);
  };

  const getSlugData = (data: MessageAttributes[]) => {
    const slugsArr = data
      .map((message) => message.touchpoint_ids_in_links)
      .flat();
    const uniqSlugs = uniq(slugsArr);
    if (uniqSlugs && uniqSlugs.length) {
      uniqSlugs.forEach((slug: string | undefined) => {
        if (slug) {
          getTouchpointData(slug);
        }
      });
    }
  };

  const onInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(event.target.value);
  };

  const onSendMessage = async () => {
    setInputText('');
    const msgData = await sendMessage(activeRoom!.id, inputText);
    if (msgData) {
      setMessagesData(msgData);
    }
  };
  const onScroll = (event: React.UIEvent<HTMLElement>) => {
    setIsScrolled(event.currentTarget.scrollTop < -100);
  };

  const onScrollBottom = () => {
    endPageRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsScrolled(false);
  };

  const isSocietyEvent = useMemo(() => {
    if (activeRoom) {
      const { created_for_type, receiver } = activeRoom;
      const { touchpointable_type } = parseResponse(
        receiver as OpportunityResponse
      );
      return created_for_type !== null && touchpointable_type === 'Event';
    }
  }, [activeRoom]);

  const actionMenuOptions = useMemo(() => {
    let options: ActionOption[] = [];
    if (activeRoom) {
      const {
        muted_at,
        id: roomId,
        blocked,
        receiver,
        blocked_by,
      } = activeRoom;
      const participable_id = receiver?.data?.attributes?.id;
      options = [
        {
          id: '1',
          label: muted_at ? 'Unmute conversation' : 'Mute conversation',
          icon: 'icon_volume-mute',
          onClick: () =>
            onMuteConversation(roomId, participable_id, !!muted_at),
        },
      ];
      if (!isSocietyEvent) {
        options.push({
          id: '2',
          label: 'Delete',
          icon: 'trash',
          onClick: () => onDeleteConversation(roomId),
          danger: true,
        });
      }
      if (
        !isSocietyEvent &&
        !(
          (blocked_by?.data.type === 'student_society' ||
            blocked_by?.data.type === 'company') &&
          blocked
        )
      ) {
        options.push({
          id: '3',
          label: blocked ? 'Unblock' : 'Block',
          icon: 'icon_ban',
          onClick: () => onBlockConversation(roomId, participable_id, blocked),
          danger: true,
        });
      }
    }
    return options as ActionOption[];
  }, [activeRoom, isSocietyEvent]);

  const showParticipants = () => {
    if (isSocietyEvent) {
      openParticipantsModal();
    }
  };

  const onHeaderClick = () => {
    if (isSocietyEvent) {
      showParticipants();
    } else if (activeRoom) {
      const { public_url } = activeRoom.receiver.data.attributes as
        | CompanyAttributes
        | StudentSocietyAttributes;
      if (public_url) {
        window.open(public_url, '_blank');
      }
    }
  };

  const removeLinks = (text: string) => {
    return text.replace(URLREGEXPATTERN, '');
  };

  const renderChats = useMemo(() => {
    return messagesData.map((message, index: number) => {
      const showDate =
        index === 0 ||
        format(new Date(message.created_at), 'P') !==
          format(new Date(messagesData[index - 1].created_at), 'P');

      const attachedOpportunityId = message?.touchpoint_id || '';
      const touchpoint = parseResponse(
        message?.touchpoint ?? ({} as TouchpointMessageResponse)
      );
      const textWithoutLinks = removeLinks(message.value);
      return (
        <div key={message.id}>
          {message.value.trim().length > 0 && showDate && (
            <div className={styles.messageDate}>
              <p>{getProcessedDate(new Date(message.created_at), true)}</p>
            </div>
          )}
          {textWithoutLinks.trim().length > 0 && (
            <ChatBubble
              message={textWithoutLinks.trim()}
              isReceived={message.show_on_right}
              senderIcon={message.sender_icon_url}
              senderName={message.sender_name}
              messageType={roomData?.created_for_type}
            />
          )}

          {message.touchpoint_ids_in_links &&
            message.touchpoint_ids_in_links.length > 0 &&
            message.touchpoint_ids_in_links.map((slug, index) => {
              return (
                <TouchpointMessage
                  key={index}
                  showOnRight={message.show_on_right}
                  showSave={true}
                  link={message.touchpoint_ids_in_links}
                  touchpointData={touchpointCachedData[slug]}
                  isSender={message.show_on_right}
                  isLoading={!touchpointCachedData[slug]}
                  senderIcon={message.sender_icon_url}
                  senderName={message.sender_name}
                  messageType={roomData?.created_for_type}
                />
              );
            })}
          {/* todo Ahmad : Can we remove loadMessages on mutateMessages
when touchpoint is being save , for attachOpportunityId case?
I have removed it from the above case. */}
          {attachedOpportunityId ? (
            <TouchpointMessage
              showSave={true}
              touchpointData={touchpoint}
              isSender={message.show_on_right}
              mutateMessages={loadMessages}
              senderIcon={message.sender_icon_url}
              senderName={message.sender_name}
              messageType={roomData?.created_for_type}
            />
          ) : null}
        </div>
      );
    });
  }, [messagesData, touchpointCachedData]);

  return (
    <>
      {isLoading && <Spinner showBackDrop />}
      <div className={cn(styles.chatWindow, className)}>
        {activeRoom && (
          <>
            <div
              className={cn(styles.chatWindowHeader, {
                [styles.scrolled]: isScrolled,
              })}
            >
              <MessageAvatar size="small" room={activeRoom} />
              <p className={styles.name} onClick={onHeaderClick}>
                {displayName}
              </p>
              {activeRoom.muted_at && (
                <Icon
                  className={styles.attrIcon}
                  iconName="icon_volume-mute"
                  size="small"
                />
              )}
              {activeRoom.blocked && (
                <Icon
                  className={styles.attrIcon}
                  iconName="icon_ban"
                  size="small"
                />
              )}
              <ActionMenu
                className={styles.actionMenu}
                options={actionMenuOptions as Array<ActionOption>}
              />
            </div>
            <div
              id="candidate-chat-container"
              className={styles.chatWindowContent}
              onScroll={onScroll}
            >
              {isMessagesFetching ? (
                <div className={styles.chatWindowContentSpinner}>
                  <Spinner size="small" />
                </div>
              ) : (
                <>
                  <div ref={endPageRef}></div>
                  <div>
                    <ReceiverInfoCard
                      room={activeRoom}
                      showParticipants={showParticipants}
                    />
                    {isScrolled ? (
                      <IconButton
                        iconName="chevron-down"
                        onClick={onScrollBottom}
                        className={styles.scrollButton}
                      />
                    ) : null}
                    {renderChats}
                  </div>
                </>
              )}
              <div ref={endPageRef}></div>
            </div>

            <div
              className={cn(styles.chatWindowFooter, {
                [styles.scrolled]: isScrolled,
              })}
            >
              <TextArea
                className={styles.inputBox}
                placeholder="Message"
                initialHeight="46px"
                isDynamic={true}
                value={inputText}
                onChange={onInput}
              />
              {inputText && (
                <IconButton
                  className={styles.sendCta}
                  iconClassName={styles.sendIcon}
                  onClick={onSendMessage}
                  iconName="icon_arrow-up-2"
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

ChatWindow.displayName = 'ChatWindow';
